<template>
  <article v-if="embedUrl" class="issuu-embed">
    <h2 v-if="title" class="text-lg font-bold leading-none uppercase md:text-xl lg:text-2xl mb-4 md:mb-6">{{ title }}</h2>
    <!--
      Gate the embed behind Cookiebot marketing consent with Cookiebot's manual markup
      (data-cookieconsent + data-cookieblock-src, plus the cookieconsent-optin/optout classes
      Cookiebot toggles), the same strategy the CMS applies server-side to YouTube/Vimeo.
      Bypassed in dev, mirroring the server-side devMode bypass, so the embed still renders
      locally without Cookiebot present.
    -->
    <div v-if="!isDev" class="cookieconsent-optout-marketing">
      <p>{{ t_('cookieBlocked') }}</p>
      <span
        ><a href="#" @click.prevent="renewConsent">{{ t_('cookieRenewLink') }}</a> {{ t_('cookieRenewText') }}</span
      >
    </div>
    <iframe
      :title="title || 'Issuu embed'"
      :src="isDev ? embedUrl : undefined"
      :data-cookieconsent="isDev ? undefined : 'marketing'"
      :data-cookieblock-src="isDev ? undefined : embedUrl"
      :class="{ 'cookieconsent-optin-marketing': !isDev }"
      allow="clipboard-write; autoplay; encrypted-media; fullscreen; picture-in-picture"
      sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox allow-forms"
      allowfullscreen="true"
      class="issuu-embed__iframe"
    />
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { setupTranslationPrefixer } from '~/composables/util'

interface Props {
  title?: string
  url?: string
}

const props = defineProps<Props>()

const { t_ } = setupTranslationPrefixer('Issuu')

// Mirror the server-side CookiebotTrait devMode bypass: in dev the embed loads directly,
// in production it is gated behind Cookiebot marketing consent.
const isDev = import.meta.dev

// Re-open the Cookiebot consent dialog so the visitor can grant marketing cookies.
function renewConsent() {
  window.Cookiebot?.renew()
}

// Only render embeds pointing at Issuu; anything else is ignored so editors
// cannot inject arbitrary iframes through this block.
const ALLOWED_HOSTS = ['issuu.com', 'www.issuu.com', 'e.issuu.com']

/**
 * Accepts both the Issuu share URL (https://issuu.com/<user>/docs/<doc>) and
 * the embed URL (https://e.issuu.com/embed.html?d=<doc>&u=<user>&...).
 *
 * For an embed URL we keep the full query string intact, because editors rely
 * on Issuu's display params (backgroundColor, hideIssuuLogo, logoImageUrl,
 * hideShareButton, showOtherPublicationsAsSuggestions, ...) to match the embed
 * to the page. Only the share URL, which carries no such params, is expanded to
 * a minimal embed src. Returns undefined for any non-Issuu or unparseable URL so
 * nothing is rendered.
 */
const embedUrl = computed<string | undefined>(() => {
  if (!props.url) {
    return undefined
  }

  let parsed: URL
  try {
    parsed = new URL(props.url)
  } catch {
    return undefined
  }

  if (!ALLOWED_HOSTS.includes(parsed.hostname)) {
    return undefined
  }

  if (parsed.hostname === 'e.issuu.com') {
    // Already an embed URL: require document + user, then pass every param
    // through untouched (only forcing https to avoid mixed content).
    if (!parsed.searchParams.get('d') || !parsed.searchParams.get('u')) {
      return undefined
    }
    parsed.protocol = 'https:'
    return parsed.toString()
  }

  // Share URL: path is /<user>/docs/<doc>; build the minimal embed src.
  const segments = parsed.pathname.split('/').filter(Boolean)
  const docsIndex = segments.indexOf('docs')
  if (docsIndex > 0 && segments[docsIndex + 1]) {
    const params = new URLSearchParams({ d: segments[docsIndex + 1], u: segments[0] })
    return `https://e.issuu.com/embed.html?${params.toString()}`
  }

  return undefined
})
</script>

<style lang="scss" scoped>
.issuu-embed__iframe {
  display: block;
  width: 100%;
  // Mirror Issuu's own embed sizing (padding-top: max(60%, 326px)): a 60% aspect ratio,
  // clamped to a minimum height so it stays usable on narrow viewports.
  aspect-ratio: 100 / 60;
  min-height: 326px;
  border: none;
  // Cookiebot forces `display: initial` (= inline) when consent is given; vertical-align
  // removes the inline baseline gap that would otherwise appear below the iframe.
  vertical-align: top;
}
</style>
